import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  useQpmCatalogGetActiveReleaseQuery,
  useQpmCatalogListQuery,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import type { CatalogListItem } from "@/services/backend/qpm/catalog/types/catalog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import t from "@/lang/lang";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import {
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useNavigate } from "react-router";
import { ArrowDownUp, Check, ExternalLink } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { CreateNewCatalogDialog } from "@/routes/base-data/processes/qpm/catalog/_components/create-new-catalog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/shared/components/ui/hover-card";
import { getRTKQueryBackendErrorCode } from "@/shared/components/errors/parse-r-t-k-query-error";
import { SeedIfEmpty } from "@/routes/base-data/processes/qpm/catalog/_components/seed-if-empty";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { CustomerSelectFrom } from "@/services/backend/customers/components/customer-select-from";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { Label } from "@/shared/components/ui/label";
import { useCustomersCustomerListQuery } from "@/services/backend/customers/api/customer-api";
import { FilterMode } from "@/shared/lib/filter/filter";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/shared/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { CustomerViewModel } from "@/services/backend/customers/types/customer";

type CatalogSortField = "name" | "createdAt" | "updatedAt";
type CatalogSortDirection = "asc" | "desc";
const CATALOG_PAGE_SIZE = 10;

export function CatalogListTablePage() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <CardHeader>
          <CardTitle>{t("Aktuell aktiver Katalog")}</CardTitle>
        </CardHeader>
        <CardContent className="mb-2">
          <SeedIfEmpty />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Name")}</TableHead>
                <TableHead>{t("Beschreibung")}</TableHead>
                <TableHead>{t("Erstellt am")}</TableHead>
                <TableHead className="text-end">{t("aktiv seit")}</TableHead>
                <TableHead className="text-end">{t("Aktionen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <ActiveTableRow />
            </TableBody>
          </Table>
        </CardContent>
      </div>
      <div>
        <CardHeader>
          <CardTitle>{t("Kataloge")}</CardTitle>
        </CardHeader>
        <CatalogListSections />
      </div>
    </div>
  );
}

function ActiveTableRow() {
  const navigate = useNavigate();
  const {
    data: catalog,
    error,
    isLoading,
  } = useQpmCatalogGetActiveReleaseQuery({});

  if (error) {
    const backendCode = getRTKQueryBackendErrorCode(error);

    // Backend returns this as 404 when no catalog is active.
    if (backendCode === "ERR_NO_ACTIVE_CATALOG_SET") {
      return (
        <TableRow>
          <TableCell className="text-center" colSpan={5}>
            -- {t("Kein aktiver Katalog vorhanden!")} --
          </TableCell>
        </TableRow>
      );
    }

    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <TableRow>
        <TableCell>
          <Skeleton className="h-10 w-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-10 w-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-10 w-full" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-10 w-full" />
        </TableCell>
      </TableRow>
    );
  }

  if (!catalog || !catalog.release?.catalogId) {
    return (
      <TableRow>
        <TableCell className="text-center" colSpan={5}>
          -- {t("Kein aktiver Katalog vorhanden!")} --
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell
        className="text-primary text-base font-bold hover:cursor-pointer hover:underline"
        onClick={() => navigate(`${catalog.catalog.id}`)}
      >
        {catalog.catalog.name}
      </TableCell>
      <TableCell className="max-w-[350px] truncate">
        <HoverCard>
          <HoverCardTrigger className="overflow-hidden text-ellipsis">
            {catalog.catalog.description}
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            className="max-h-[230px] w-fit max-w-screen-sm overflow-hidden text-wrap text-ellipsis"
          >
            {catalog.catalog.description}
          </HoverCardContent>
        </HoverCard>
      </TableCell>
      <TableCell>
        {formatDate(catalog.catalog.createdAt)} -{" "}
        {formatTime(catalog.catalog.createdAt)}
      </TableCell>
      <TableCell className="text-end">
        {formatDate(catalog.release.publishedAt)}
      </TableCell>
      <TableCell className="text-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="mr-1"
                size="icon"
                variant="outline"
                onClick={() => navigate(`${catalog.catalog.id}`)}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("Öffnen")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}

function CatalogListSections() {
  const navigate = useNavigate();
  const {
    error,
    isLoading,
    hasCatalogs,
    selectedCustomerId,
    setSelectedCustomerId,
    customerOptions,
    globalCatalogs,
    customerCatalogs,
    globalPageIndex,
    setGlobalPageIndex,
    customerPageIndex,
    setCustomerPageIndex,
    globalTotalItems,
    globalTotalPages,
    customerTotalItems,
    customerTotalPages,
    globalSortField,
    setGlobalSortField,
    globalSortDirection,
    setGlobalSortDirection,
    customerSortField,
    setCustomerSortField,
    customerSortDirection,
    setCustomerSortDirection,
  } = useCatalogListSectionsData();

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <CardContent>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    );
  }

  if (!hasCatalogs) {
    return (
      <CardContent>-- {t("Es sind keine Kataloge vorhanden!")} --</CardContent>
    );
  }

  return (
    <CardContent className="flex flex-col gap-6">
      <CatalogSectionTable
        title={t("Allgemeine Kataloge")}
        catalogs={globalCatalogs}
        emptyText={t("Es sind keine Kataloge vorhanden!")}
        pageIndex={globalPageIndex}
        totalPages={globalTotalPages}
        totalItems={globalTotalItems}
        sortField={globalSortField}
        sortDirection={globalSortDirection}
        onSortFieldChange={setGlobalSortField}
        onSortDirectionChange={setGlobalSortDirection}
        onPageChange={setGlobalPageIndex}
        actions={<CreateNewCatalogDialog />}
        onOpen={(catalogID) => navigate(`${catalogID}`)}
      />

      <CatalogSectionTable
        title={t("Kundenkataloge")}
        catalogs={customerCatalogs}
        emptyText={t("Es sind keine Kundenkataloge vorhanden!")}
        pageIndex={customerPageIndex}
        totalPages={customerTotalPages}
        totalItems={customerTotalItems}
        sortField={customerSortField}
        sortDirection={customerSortDirection}
        onSortFieldChange={setCustomerSortField}
        onSortDirectionChange={setCustomerSortDirection}
        onPageChange={setCustomerPageIndex}
        actions={
          <CreateNewCatalogDialog
            requireCustomer
            triggerLabel={t("Kundenkatalog hinzufügen")}
            dialogTitle={t("Neuen Kundenkatalog erstellen")}
          />
        }
        filter={
          <div className="flex w-full max-w-md flex-col gap-1">
            <Label>{t("Kunde filtern")}</Label>
            <CustomerSelectFrom
              value={selectedCustomerId}
              customerOptions={customerOptions}
              showUnsetOption
              hideAddress
              onValueChange={(customerId) =>
                setSelectedCustomerId(customerId || NullUUID)
              }
            />
          </div>
        }
        showCustomer
        onOpen={(catalogID) => navigate(`${catalogID}`)}
      />
    </CardContent>
  );
}

function useCatalogListSectionsData() {
  const { companyId } = useActiveCompany();
  const [selectedCustomerId, setSelectedCustomerId] =
    useState<string>(NullUUID);
  const [globalPageIndex, setGlobalPageIndex] = useState(0);
  const [customerPageIndex, setCustomerPageIndex] = useState(0);
  const [globalSortField, setGlobalSortField] =
    useState<CatalogSortField>("name");
  const [globalSortDirection, setGlobalSortDirection] =
    useState<CatalogSortDirection>("asc");
  const [customerSortField, setCustomerSortField] =
    useState<CatalogSortField>("name");
  const [customerSortDirection, setCustomerSortDirection] =
    useState<CatalogSortDirection>("asc");
  const globalOrderBy = useMemo(
    () => [{ field: globalSortField, dsc: globalSortDirection === "desc" }],
    [globalSortDirection, globalSortField],
  );
  const customerOrderBy = useMemo(
    () => [{ field: customerSortField, dsc: customerSortDirection === "desc" }],
    [customerSortDirection, customerSortField],
  );

  const {
    data: globalList,
    error: globalError,
    isLoading: globalLoading,
  } = useQpmCatalogListQuery({
    company: { active: true, values: [companyId] },
    customer: { active: true, mode: FilterMode.IsNull },
    orderBy: globalOrderBy,
    limit: CATALOG_PAGE_SIZE,
    offset: globalPageIndex * CATALOG_PAGE_SIZE,
  });
  const {
    data: customerList,
    error: customerError,
    isLoading: customerLoading,
  } = useQpmCatalogListQuery({
    company: { active: true, values: [companyId] },
    customer:
      selectedCustomerId === NullUUID
        ? { active: true, mode: FilterMode.NotNull }
        : { active: true, values: [selectedCustomerId] },
    orderBy: customerOrderBy,
    limit: CATALOG_PAGE_SIZE,
    offset: customerPageIndex * CATALOG_PAGE_SIZE,
  });
  const { data: customerFilterList, error: customerFilterError } =
    useQpmCatalogListQuery({
      company: { active: true, values: [companyId] },
      customer: { active: true, mode: FilterMode.NotNull },
      orderBy: [{ field: "name", dsc: false }],
      limit: 10_000,
      offset: 0,
    });
  const {
    data: customersList,
    error: customersError,
    isLoading: customersLoading,
  } = useCustomersCustomerListQuery({
    name: {
      active: true,
      mode: FilterMode.Like,
      values: [""],
    },
    limit: 10_000,
    offset: 0,
  });

  const globalTotalItems = globalList?.total ?? 0;
  const globalTotalPages = Math.max(
    1,
    Math.ceil(globalTotalItems / CATALOG_PAGE_SIZE),
  );
  const customerTotalItems = customerList?.total ?? 0;
  const customerTotalPages = Math.max(
    1,
    Math.ceil(customerTotalItems / CATALOG_PAGE_SIZE),
  );

  useEffect(() => {
    if (globalPageIndex > globalTotalPages - 1) {
      setGlobalPageIndex(Math.max(globalTotalPages - 1, 0));
    }
  }, [globalPageIndex, globalTotalPages]);

  useEffect(() => {
    if (customerPageIndex > customerTotalPages - 1) {
      setCustomerPageIndex(Math.max(customerTotalPages - 1, 0));
    }
  }, [customerPageIndex, customerTotalPages]);

  useEffect(() => {
    setCustomerPageIndex(0);
  }, [selectedCustomerId]);

  useEffect(() => {
    setGlobalPageIndex(0);
  }, [globalSortDirection, globalSortField]);

  useEffect(() => {
    setCustomerPageIndex(0);
  }, [customerSortDirection, customerSortField]);

  const globalCatalogs = useMemo(
    () => globalList?.items ?? [],
    [globalList?.items],
  );
  const customerCatalogs = useMemo(
    () => customerList?.items ?? [],
    [customerList?.items],
  );
  const customerIdsWithCatalogs = useMemo(
    () =>
      Array.from(
        new Set(
          (customerFilterList?.items ?? [])
            .map((entry) => entry.customerId)
            .filter((customerId): customerId is string => !!customerId),
        ),
      ),
    [customerFilterList?.items],
  );
  const customerOptions = useMemo(() => {
    const customersById = new Map(
      (customersList?.items ?? []).map((customer) => [customer.id, customer]),
    );
    return customerIdsWithCatalogs
      .map((customerId) => customersById.get(customerId))
      .filter((customer): customer is CustomerViewModel => !!customer)
      .map((customer) => ({
        id: customer.id,
        name: customer.name,
      }));
  }, [customerIdsWithCatalogs, customersList?.items]);

  return {
    error:
      globalError ?? customerError ?? customerFilterError ?? customersError,
    isLoading: globalLoading || customerLoading || customersLoading,
    hasCatalogs: globalTotalItems + customerTotalItems > 0,
    selectedCustomerId,
    setSelectedCustomerId,
    customerOptions,
    globalCatalogs,
    customerCatalogs,
    globalPageIndex,
    setGlobalPageIndex,
    customerPageIndex,
    setCustomerPageIndex,
    globalTotalItems,
    globalTotalPages,
    customerTotalItems,
    customerTotalPages,
    globalSortField,
    setGlobalSortField,
    globalSortDirection,
    setGlobalSortDirection,
    customerSortField,
    setCustomerSortField,
    customerSortDirection,
    setCustomerSortDirection,
  };
}

function CatalogSectionTable({
  title,
  catalogs,
  emptyText,
  pageIndex,
  totalPages,
  totalItems,
  sortField,
  sortDirection,
  onSortFieldChange,
  onSortDirectionChange,
  onPageChange,
  actions,
  filter,
  showCustomer,
  onOpen,
}: {
  title: string;
  catalogs: CatalogListItem[];
  emptyText: string;
  pageIndex: number;
  totalPages: number;
  totalItems: number;
  sortField: CatalogSortField;
  sortDirection: CatalogSortDirection;
  onSortFieldChange: (field: CatalogSortField) => void;
  onSortDirectionChange: (direction: CatalogSortDirection) => void;
  onPageChange: (nextPage: number) => void;
  actions: ReactNode;
  filter?: ReactNode;
  showCustomer?: boolean;
  onOpen: (catalogID: string) => void;
}) {
  const columnSpan = showCustomer ? 6 : 5;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <CatalogSortButton
            field={sortField}
            direction={sortDirection}
            onFieldChange={onSortFieldChange}
            onDirectionChange={onSortDirectionChange}
          />
          {actions}
        </div>
      </div>
      {filter}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Name")}</TableHead>
            {showCustomer && <TableHead>{t("Kunde")}</TableHead>}
            <TableHead>{t("Beschreibung")}</TableHead>
            <TableHead className="text-start">{t("Erstellt am")}</TableHead>
            <TableHead className="text-start">{t("Aktualisiert am")}</TableHead>
            <TableHead className="text-end">{t("Aktionen")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {catalogs.length <= 0 && (
            <TableRow>
              <TableCell className="text-center" colSpan={columnSpan}>
                -- {emptyText} --
              </TableCell>
            </TableRow>
          )}
          {catalogs.map((entry) => (
            <CatalogListRow
              key={entry.id}
              id={entry.id}
              name={entry.name}
              description={entry.description}
              createdAt={entry.createdAt}
              updatedAt={entry.updatedAt}
              customerId={showCustomer ? entry.customerId : undefined}
              onOpen={onOpen}
            />
          ))}
        </TableBody>
      </Table>
      <SectionPagination
        title={title}
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function CatalogSortButton({
  field,
  direction,
  onFieldChange,
  onDirectionChange,
}: {
  field: CatalogSortField;
  direction: CatalogSortDirection;
  onFieldChange: (field: CatalogSortField) => void;
  onDirectionChange: (direction: CatalogSortDirection) => void;
}) {
  const fieldLabels: Record<CatalogSortField, string> = {
    name: t("Name"),
    createdAt: t("Erstellt am"),
    updatedAt: t("Aktualisiert am"),
  };
  const fieldLabel = fieldLabels[field];
  const directionLabel =
    direction === "asc" ? t("Aufsteigend") : t("Absteigend");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <ArrowDownUp className="h-4 w-4" />
          {t("Sortieren")}: {fieldLabel} ({directionLabel})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t("Sortierfeld")}</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onFieldChange("name")}>
          <span className="flex-1">{t("Name")}</span>
          {field === "name" && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onFieldChange("createdAt")}>
          <span className="flex-1">{t("Erstellt am")}</span>
          {field === "createdAt" && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onFieldChange("updatedAt")}>
          <span className="flex-1">{t("Aktualisiert am")}</span>
          {field === "updatedAt" && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Reihenfolge")}</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onDirectionChange("asc")}>
          <span className="flex-1">{t("Aufsteigend")}</span>
          {direction === "asc" && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDirectionChange("desc")}>
          <span className="flex-1">{t("Absteigend")}</span>
          {direction === "desc" && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SectionPagination({
  title,
  pageIndex,
  totalPages,
  totalItems,
  onPageChange,
}: {
  title: string;
  pageIndex: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (nextPage: number) => void;
}) {
  const currentPage = pageIndex + 1;
  const items = buildPaginationItems(currentPage, totalPages);
  const canPrev = pageIndex > 0;
  const canNext = pageIndex < totalPages - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-muted-foreground text-sm">
        {t("Seite")} {currentPage} {t("von")} {totalPages} ({totalItems} {title}
        )
      </div>
      <Pagination className="mx-0 w-auto justify-start">
        <PaginationContent>
          <PaginationItem>
            <PaginationLink
              href="#"
              aria-label={t("Zur vorherigen Seite")}
              size="default"
              className={`px-3 ${!canPrev ? "pointer-events-none opacity-50" : ""}`}
              onClick={(event) => {
                event.preventDefault();
                if (canPrev) {
                  onPageChange(pageIndex - 1);
                }
              }}
            >
              {t("Zurück")}
            </PaginationLink>
          </PaginationItem>
          {items.map((item) =>
            item === "ellipsis-left" || item === "ellipsis-right" ? (
              <PaginationItem key={item}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={`page-${item}`}>
                <PaginationLink
                  href="#"
                  isActive={item === currentPage}
                  onClick={(event) => {
                    event.preventDefault();
                    onPageChange(item - 1);
                  }}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationLink
              href="#"
              aria-label={t("Zur nächsten Seite")}
              size="default"
              className={`px-3 ${!canNext ? "pointer-events-none opacity-50" : ""}`}
              onClick={(event) => {
                event.preventDefault();
                if (canNext) {
                  onPageChange(pageIndex + 1);
                }
              }}
            >
              {t("Weiter")}
            </PaginationLink>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}

function buildPaginationItems(
  currentPage: number,
  totalPages: number,
): Array<number | "ellipsis-left" | "ellipsis-right"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: Array<number | "ellipsis-left" | "ellipsis-right"> = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    items.push("ellipsis-left");
  }
  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }
  if (end < totalPages - 1) {
    items.push("ellipsis-right");
  }

  items.push(totalPages);
  return items;
}

function CatalogListRow({
  id,
  name,
  description,
  createdAt,
  updatedAt,
  onOpen,
  customerId,
}: {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  onOpen: (id: string) => void;
  customerId?: string;
}) {
  return (
    <TableRow>
      <TableCell
        className="text-primary text-base hover:cursor-pointer hover:underline"
        onClick={() => onOpen(id)}
      >
        {name}
      </TableCell>
      {customerId !== undefined && (
        <TableCell>
          <CustomerName customerId={customerId} />
        </TableCell>
      )}
      <TableCell className="max-w-[350px] truncate">
        <HoverCard>
          <HoverCardTrigger className="overflow-hidden text-ellipsis">
            {description}
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            className="max-h-[230px] w-fit max-w-screen-sm overflow-hidden text-wrap text-ellipsis"
          >
            {description}
          </HoverCardContent>
        </HoverCard>
      </TableCell>
      <TableCell className="text-start">
        {formatDate(createdAt)} - {formatTime(createdAt)}
      </TableCell>
      <TableCell className="text-start">
        {formatDate(updatedAt)} - {formatTime(updatedAt)}
      </TableCell>
      <TableCell className="text-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="mr-1"
                size="icon"
                variant="outline"
                onClick={() => onOpen(id)}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("Öffnen")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}
